import {AABB, VECTOR3} from '../../index.js';

/**
 * Check if two AABBs collide with each other (if they have a point, a segment, a surface or an area in common).
 * @param {AABB} $a The first AABB to compare.
 * @param {AABB} $b The second AABB to compare.
 * @returns {boolean}
 */
function aabbcollide($a, $b) {

    if ($a.xmax < $b.xmin
    || $a.ymax < $b.ymin
    || $a.zmax < $b.zmin
    || $b.xmax < $a.xmin
    || $b.ymax < $a.ymin
    || $b.zmax < $a.zmin) {

        return false;
    }

    return true;
}

/**
 * Check if two AABBs strictly collide with each other (if they have an area in common).
 * @param {AABB} $a The first AABB to compare.
 * @param {AABB} $b The second AABB to compare.
 * @returns {boolean}
 */
function aabbcollidestrict($a, $b) {

    if ($a.xmax <= $b.xmin
    || $a.ymax <= $b.ymin
    || $a.zmax <= $b.zmin
    || $b.xmax <= $a.xmin
    || $b.ymax <= $a.ymin
    || $b.zmax <= $a.zmin) {

        return false;
    }

    return true;
}

/**
 * Create a new AABB minimum container from two given AABB.
 * @param {AABB} $a The first AABB to contain.
 * @param {AABB} $b The second AABB to contain.
 * @returns {AABB}
 */
function aabbminimumcontainer($a, $b) {

    const xmin = Math.min($a.xmin, $b.xmin);
    const ymin = Math.min($a.ymin, $b.ymin);
    const zmin = Math.min($a.zmin, $b.zmin);

    const xmax = Math.max($a.xmax, $b.xmax);
    const ymax = Math.max($a.ymax, $b.ymax);
    const zmax = Math.max($a.zmax, $b.zmax);

    const min = VECTOR3.create([xmin, ymin, zmin]);
    const max = VECTOR3.create([xmax, ymax, zmax]);

    return new AABB(min, max);
}

/**
 * Check if two AABBs strictly overlap with each other on the x-axis (if they have an area in common).
 * @param {AABB} $a The first AABB to compare.
 * @param {AABB} $b The second AABB to compare.
 * @returns {boolean}
 */
function aabboverlapstrictx($a, $b) {

    if ($a.xmin >= $b.xmax
    || $a.xmax <= $b.xmin) {

        return false;
    }

    return true;
}

/**
 * Check if two AABBs strictly overlap with each other on the y-axis (if they have an area in common).
 * @param {AABB} $a The first AABB to compare.
 * @param {AABB} $b The second AABB to compare.
 * @returns {boolean}
 */
function aabboverlapstricty($a, $b) {

    if ($a.ymin >= $b.ymax
    || $a.ymax <= $b.ymin) {

        return false;
    }

    return true;
}

/**
 * Check if two AABBs strictly overlap with each other on the z-axis (if they have an area in common).
 * @param {AABB} $a The first AABB to compare.
 * @param {AABB} $b The second AABB to compare.
 * @returns {boolean}
 */
function aabboverlapstrictz($a, $b) {

    if ($a.zmin >= $b.zmax
    || $a.zmax <= $b.zmin) {

        return false;
    }

    return true;
}

/**
 * Gets the time ratio of the collision between two AABBs (with continuous detection collision).
 * @param {AABB} $abefore The first AABB at its initial state.
 * @param {AABB} $aafter The first AABB at its final state.
 * @param {AABB} $bbefore The second AABB at its initial state.
 * @param {AABB} $bafter The second AABB at its final state.
 * @returns {number}
 */
function aabbsweptcollision($abefore, $aafter, $bbefore, $bafter) {

    const velocitya = VECTOR3.subtract($aafter.translation, $abefore.translation);
    const velocityb = VECTOR3.subtract($bafter.translation, $bbefore.translation);

    const [velocityrelativex, velocityrelativey, velocityrelativez] = VECTOR3.subtract(velocitya, velocityb);

    const collidera = $abefore;
    const colliderb = $bbefore;

    let entrytimex = Number.NEGATIVE_INFINITY;
    let entrytimey = Number.NEGATIVE_INFINITY;
    let entrytimez = Number.NEGATIVE_INFINITY;

    let exittimex = Number.POSITIVE_INFINITY;
    let exittimey = Number.POSITIVE_INFINITY;
    let exittimez = Number.POSITIVE_INFINITY;

    if (velocityrelativex !== 0) {

        const entryx = (velocityrelativex > 0) ? (colliderb.xmin - collidera.xmax) : (colliderb.xmax - collidera.xmin);
        const exitx = (velocityrelativex > 0) ? (colliderb.xmax - collidera.xmin) : (colliderb.xmin - collidera.xmax);

        const velocityrelativexinverse = 1 / velocityrelativex;

        entrytimex = entryx * velocityrelativexinverse;
        exittimex = exitx * velocityrelativexinverse;
    }

    if (entrytimex < 0 && (collidera.xmax < colliderb.xmin || colliderb.xmax < collidera.xmin)) {

        return 1;
    }

    if (velocityrelativey !== 0) {

        const entryy = (velocityrelativey > 0) ? (colliderb.ymin - collidera.ymax) : (colliderb.ymax - collidera.ymin);
        const exity = (velocityrelativey > 0) ? (colliderb.ymax - collidera.ymin) : (colliderb.ymin - collidera.ymax);

        const velocityrelativeyinverse = 1 / velocityrelativey;

        entrytimey = entryy * velocityrelativeyinverse;
        exittimey = exity * velocityrelativeyinverse;
    }

    if (entrytimey < 0 && (collidera.ymax < colliderb.ymin || colliderb.ymax < collidera.ymin)) {

        return 1;
    }

    if (velocityrelativez !== 0) {

        const entryz = (velocityrelativez > 0) ? (colliderb.zmin - collidera.zmax) : (colliderb.zmax - collidera.zmin);
        const exitz = (velocityrelativez > 0) ? (colliderb.zmax - collidera.zmin) : (colliderb.zmin - collidera.zmax);

        const velocityrelativezinverse = 1 / velocityrelativez;

        entrytimez = entryz * velocityrelativezinverse;
        exittimez = exitz * velocityrelativezinverse;
    }

    if (entrytimez < 0 && (collidera.zmax < colliderb.zmin || colliderb.zmax < collidera.zmin)) {

        return 1;
    }

    if (entrytimex < 0 && entrytimey < 0 && entrytimez < 0) {

        return 1;
    }

    if (entrytimex > 1
    || entrytimey > 1
    || entrytimez > 1) {

        return 1;
    }

    const entrytime = Math.max(Math.max(entrytimex, entrytimey), entrytimez);
    const exittime = Math.min(Math.min(exittimex, exittimey), exittimez);

    if (entrytime > exittime) {

        return 1;
    }

    return entrytime;
}

export {

    aabbcollide,
    aabbcollidestrict,
    aabbminimumcontainer,
    aabboverlapstrictx,
    aabboverlapstricty,
    aabboverlapstrictz,
    aabbsweptcollision
};
